/**
 * Float32 → PCM16, and anti-aliased resampling to the provider rate.
 *
 * ── WHY NOT "TAKE EVERY SECOND SAMPLE" ─────────────────────────────────────────
 *
 * Decimating 48 kHz to 24 kHz by dropping alternate samples folds everything
 * above 12 kHz back down into the speech band as aliasing. It does not sound
 * like missing treble; it sounds like a metallic rasp sitting on top of the
 * voice, and it corrupts exactly the spectral content that later acoustic
 * evidence would measure. So the signal is low-passed before it is decimated.
 *
 * ── PURE, AND SEPARATE FROM THE WORKLET ────────────────────────────────────────
 *
 * None of this touches an AudioContext. That is deliberate: these are the
 * numerical steps that decide what the model hears and what the analyzers
 * measure, and they must be testable against known waveforms rather than only
 * observable through a browser.
 */

export const PROVIDER_RATE = 24000;

/** Signed 16-bit range. -32768 is representable; +32768 is not. */
const INT16_MIN = -32768;
const INT16_MAX = 32767;

/**
 * Convert one Float32 sample to signed 16-bit.
 *
 * Clamped first, then scaled asymmetrically: the negative range has one more
 * step than the positive one, so scaling both by 32768 would let +1.0 overflow
 * to -32768 — a full-scale positive peak rendered as a full-scale negative one,
 * which is an audible click exactly at the loudest moments.
 */
export function floatToPcm16(sample: number): number {
  if (Number.isNaN(sample)) return 0;
  const clamped = Math.max(-1, Math.min(1, sample));
  return clamped < 0
    ? Math.max(INT16_MIN, Math.round(clamped * 32768))
    : Math.min(INT16_MAX, Math.round(clamped * 32767));
}

/** Encode Float32 frames as little-endian PCM16, endian-independently. */
export function encodePcm16(input: Float32Array): ArrayBuffer {
  const out = new ArrayBuffer(input.length * 2);
  const view = new DataView(out);
  for (let i = 0; i < input.length; i++) {
    // Explicit littleEndian=true: DataView never follows platform endianness,
    // so the wire format is identical on every device.
    view.setInt16(i * 2, floatToPcm16(input[i] as number), true);
  }
  return out;
}

/** Decode little-endian PCM16 back to Float32, for playback and for tests. */
export function decodePcm16(buffer: ArrayBuffer): Float32Array {
  const view = new DataView(buffer);
  const out = new Float32Array(Math.floor(buffer.byteLength / 2));
  for (let i = 0; i < out.length; i++) {
    out[i] = (view.getInt16(i * 2, true) as number) / 32768;
  }
  return out;
}

/**
 * A windowed-sinc low-pass, designed once for a given conversion.
 *
 * Chosen over a biquad cascade because the coefficients are inspectable and the
 * delay is exactly known — half the kernel — which matters when the resampler
 * sits in the path that timestamps speech.
 */
export function designLowPass(cutoffHz: number, sampleRate: number, taps: number): Float32Array {
  // Odd tap count keeps the delay an exact whole number of samples.
  const n = taps % 2 === 0 ? taps + 1 : taps;
  const kernel = new Float32Array(n);
  const mid = (n - 1) / 2;
  const fc = cutoffHz / sampleRate;
  let sum = 0;

  for (let i = 0; i < n; i++) {
    const x = i - mid;
    const sinc = x === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * x) / (Math.PI * x);
    // Hamming window: sidelobes low enough for speech without a long kernel.
    const w = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (n - 1));
    const v = sinc * w;
    kernel[i] = v;
    sum += v;
  }
  // Normalise to unit DC gain so the conversion cannot change loudness — an
  // amplitude shift here would land directly in RMS-based acoustic evidence.
  for (let i = 0; i < n; i++) kernel[i] = (kernel[i] as number) / sum;
  return kernel;
}

/**
 * Streaming resampler to 24 kHz.
 *
 * Holds the filter tail between calls, so consecutive packets join without a
 * discontinuity at the seam. A stateless per-packet resampler produces a click
 * at every boundary, which a VAD reads as onset.
 */
export class SpeechResampler {
  private readonly kernel: Float32Array;
  private readonly history: Float32Array;
  private historyFilled = 0;
  /** Fractional read position, carried across packets. */
  private position = 0;

  readonly ratio: number;
  readonly passthrough: boolean;

  constructor(readonly inputRate: number, readonly outputRate: number = PROVIDER_RATE) {
    this.passthrough = inputRate === outputRate;
    this.ratio = inputRate / outputRate;
    // Nyquist of the OUTPUT rate, with margin. Speech above 10 kHz carries
    // almost nothing, and a lower cutoff buys a shorter kernel.
    const cutoff = Math.min(outputRate / 2, inputRate / 2) * 0.9;
    this.kernel = this.passthrough ? new Float32Array(0) : designLowPass(cutoff, inputRate, 63);
    this.history = new Float32Array(this.kernel.length);
  }

  /** The resampler's group delay, in input samples. */
  get delaySamples(): number {
    return this.passthrough ? 0 : (this.kernel.length - 1) / 2;
  }

  process(input: Float32Array): Float32Array {
    if (this.passthrough) return input;

    const k = this.kernel.length;
    const tail = k - 1;

    // work = retained tail + this packet. Filtering starts at index `tail`, so
    // every produced sample sees a full kernel and the packet boundary is
    // invisible — a per-packet resampler with no retained tail clicks at every
    // seam, and a VAD reads a click as speech onset.
    const work = new Float32Array(this.historyFilled + input.length);
    work.set(this.history.subarray(0, this.historyFilled), 0);
    work.set(input, this.historyFilled);

    // One filtered output per input sample, aligned to the packet.
    const start = this.historyFilled;
    const filtered = new Float32Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let acc = 0;
      const base = start + i;
      for (let j = 0; j < k; j++) {
        const idx = base - j;
        if (idx >= 0) acc += (work[idx] as number) * (this.kernel[j] as number);
      }
      filtered[i] = acc;
    }

    // Linear interpolation at the carried fractional position. Aliasing was
    // already removed above, so interpolation error sits far below a
    // microphone's noise floor.
    const out: number[] = [];
    let pos = this.position;
    while (pos < filtered.length) {
      const i0 = Math.floor(pos);
      const frac = pos - i0;
      const a = filtered[i0] as number;
      const b = (filtered[i0 + 1] ?? a) as number;
      out.push(a * (1 - frac) + b * frac);
      pos += this.ratio;
    }
    // Carry the remainder into the next packet so no sample is emitted twice
    // and none is skipped.
    this.position = pos - filtered.length;

    const keep = Math.min(tail, work.length);
    this.history.set(work.subarray(work.length - keep), 0);
    this.historyFilled = keep;

    return Float32Array.from(out);
  }
}
